// Copyright (c) 2017, Shellyl_N and Authors
// license: ISC
// https://github.com/shellyln
import { Integer53 } from "red-agate-util/modules/types/Integer53";
import { Gf2Ext } from "./Gf2Ext";
import { GfPrime } from "./GfPrime";
/**
 *
 */
export class Gf2PrimeFieldHelper {
    static listPx(nth, listAll = false) {
        // GF(p^m)      's primitive polynomials P(X) are:
        // x^(p^m-1)-1  's factor that degree of m irreducible polynomial
        // (p:prime, m:degree of field extension)
        const numElements = Math.pow(2, nth); // 1 << nth
        const mask = numElements - 1;
        const zx = [];
        const pxs = [];
        zx[mask] = 1;
        zx[0] = 1;
        const n = Math.pow(2, (nth + 1));
        for (let i = 2; i < n; i++) {
            const result = Gf2PrimeFieldHelper.polyDiv(zx, Gf2PrimeFieldHelper.bitToPoly(i));
            if (result.divisible && (pxs.length === 0 ||
                pxs.every((px) => !Gf2PrimeFieldHelper.bitPolyDiv(i, px).divisible)))
                pxs.push(i);
        }
        if (listAll)
            return pxs;
        else
            return pxs.filter(v => Boolean(Integer53.bitRShift(v, nth) & 1)).filter((v) => {
                const gf = new Gf2Ext(nth, v);
                return gf.checkGenerationResult();
            });
    }
    /**
     *
     */
    static bitToPoly(x) {
        const r = [];
        x = Math.abs(x);
        const loX = (x & 0xffffffff) >>> 0;
        const hiX = (x - loX) / 4294967296;
        for (let i = 0; i < 32; i++) {
            r[i] = ((loX & (1 << i)) >>> 0) ? 1 : 0;
        }
        for (let i = 0; i < 21; i++) {
            r[32 + i] = ((hiX & (1 << i)) >>> 0) ? 1 : 0;
        }
        for (let i = 52; i > 0; i--) {
            if (r[i]) {
                r.length = i + 1;
                return r;
            }
        }
        r.length = 1;
        return r;
    }
    /**
     *
     */
    static polyToBit(x) {
        let r = 0;
        const n = Math.min(53, x.length);
        for (let i = 0; i < n; i++) {
            if (x[i])
                r += Math.pow(2, i);
        }
        return r;
    }
    /**
     *
     */
    static polyAdd(x, y) {
        // GF2 prime finite field's calculation.
        const r = [];
        r.length = Math.max(x.length, y.length);
        for (let i = 0; i < r.length; i++) {
            r[i] = (x[i] !== void (0) ? x[i] : 0) ^ (y[i] !== void (0) ? y[i] : 0);
        }
        return r;
    }
    /**
     *
     */
    static polySub(x, y) {
        // GF2 prime finite field's calculation.
        return this.polyAdd(x, y);
    }
    /**
     *
     */
    static bitPolyAdd(x, y) {
        // GF2 prime finite field's calculation.
        return Integer53.bitXor(x, y);
    }
    /**
     *
     */
    static bitPolySub(x, y) {
        // GF2 prime finite field's calculation.
        return Integer53.bitXor(x, y);
    }
    /**
     *
     */
    static polyMul(x, y) {
        // GF2 prime finite field's calculation.
        const r = [];
        r.length = x.length + y.length - 1;
        for (let i = 0; i < y.length; i++) {
            for (let j = 0; j < x.length; j++) {
                // r[j + i] is undefined but bitwise operators implicitly cast NaN as 0.
                r[j + i] ^= x[j] & y[i];
            }
        }
        return r;
    }
    /**
     *
     */
    static bitPolyMul(x, y) {
        // GF2 prime finite field's calculation.
        let r = 0;
        for (let i = 0; i < 53; i++) {
            r = Integer53.bitXor(r, x * (Integer53.bitRShift(y, i) & 1) * (Math.pow(2, i)));
        }
        return r;
    }
    /**
     *
     */
    static polyDiv(x, y, extendDividendLength = 0) {
        // GF2 prime finite field's calculation.
        x = Array.from(x);
        if (extendDividendLength > 0) {
            const ext = [];
            ext.length = extendDividendLength;
            for (let i = 0; i < extendDividendLength; i++) {
                ext[i] = 0;
            }
            x = ext.concat(x);
        }
        let ylen = y.length;
        for (let i = 0; i < ylen; i++, ylen--) {
            if (y[ylen - 1])
                break;
        }
        if (ylen === 0)
            return { q: [Number.NaN], r: [Number.NaN], divisible: false };
        const n = x.length - ylen + 1;
        const q = [];
        q.length = n;
        for (let i = 0; i < n; i++) {
            if (x[x.length - 1 - i]) {
                q[n - 1 - i] = 1;
                for (let j = 0; j < ylen; j++) {
                    x[n - 1 - i + j] ^= y[j];
                }
            }
            else {
                x[x.length - 1 - i] = 0;
            }
        }
        let divisible = true;
        const r = [];
        r.length = ylen - 1;
        for (let i = 0; i < r.length; i++) {
            if (x[i])
                divisible = false;
            r[i] = x[i];
        }
        return { q, r, divisible };
    }
    /**
     *
     */
    static bitPolyDiv(x, y, extendDividendLength = 0) {
        // GF2 prime finite field's calculation.
        if (extendDividendLength)
            x = Integer53.bitLShift(x, extendDividendLength);
        let ylen = 53;
        for (let i = 0; i < 53; i++, ylen--) {
            if (Integer53.bitRShift(y, 52 - i))
                break;
        }
        if (ylen === 0)
            return { q: Number.NaN, r: Number.NaN, divisible: false };
        const n = 53 - ylen + 1;
        let q = 0;
        for (let i = 0, m = Math.pow(2, 52); i < n; i++, m /= 2) {
            if (Math.trunc(x / m) & 1) {
                const w = Math.pow(2, (n - 1 - i));
                q += w;
                x = Integer53.bitXor(x, y * w);
            }
        }
        return { q, r: x, divisible: x === 0 };
    }
    /**
     *
     */
    static polyIsZero(x) {
        const n = Math.min(52, x.length);
        for (let i = 0; i < n; i++) {
            if (x[i])
                return true;
        }
        return false;
    }
}
export class Gf2PrimeBitPolynomialRing {
    get field() {
        if (this._field)
            return this._field;
        this._field = new GfPrime(2);
        return this._field;
    }
    listPx(nth, listAll = false) {
        return Gf2PrimeFieldHelper.listPx(nth, listAll);
    }
    getAt(x, index) {
        return Integer53.bitRShift(x, index) & 1;
    }
    setAt(x, index, v) {
        return Integer53.bitNot(Integer53.bitLShift((v & 1) ? 0 : 1, index));
    }
    slice(x, start, end) {
        return Integer53.bitAnd(Integer53.bitRShift(Integer53.MAX_INT, end - start), Integer53.bitRShift(x, start));
    }
    map(x, fn) {
        return this.mapToSelf(x, fn);
    }
    mapToSelf(x, fn) {
        for (let i = 0; i < 53; i++)
            x = this.setAt(x, i, fn(this.getAt(x, i)));
        return x;
    }
    newNthPolynomial(n) {
        return 0;
    }
    /**
     * additive identity element
     */
    get ZERO() {
        return 0;
    }
    /**
     * multiplicative identity element
     */
    get ONE() {
        return 1;
    }
    /**
     * NaN value
     */
    get NaN() {
        return Number.NaN;
    }
    /**
     * normalize a value
     */
    normalize(x) {
        return x;
    }
    /**
     * check value is NaN
     */
    isNaN(x) {
        return Number.isNaN(x);
    }
    /**
     * compare element and return true if x equals to y
     * eq(v,v)     -> true
     * eq(NaN,NaN) -> false
     */
    eq(x, y) {
        return x === y;
    }
    /**
     * compare element and return false if x equals to y
     */
    noteq(x, y) {
        return x !== y;
    }
    /**
     * negate; additive inverse (-x)
     */
    neg(x) {
        return x;
    }
    /**
     * addition (x+y)
     */
    add(x, y) {
        return Integer53.bitXor(x, y);
    }
    /**
     * subtraction (x-y)
     */
    sub(x, y) {
        return Integer53.bitXor(x, y);
    }
    /**
     * multiplication (x*y)
     */
    mul(x, y) {
        return Gf2PrimeFieldHelper.bitPolyMul(x, y);
    }
    /**
     * modulation (x%y)
     */
    mod(x, y, extendDividendLength = 0) {
        return Gf2PrimeFieldHelper.bitPolyDiv(x, y, extendDividendLength).r;
    }
    /**
     * division (x/y)
     *             ? = div(x,ZERO) ; depends on field definition
     * mul(x,inv(y)) = div(x,y)    ;  when  y != ZERO
     */
    divmod(x, y, extendDividendLength = 0) {
        return Gf2PrimeFieldHelper.bitPolyDiv(x, y, extendDividendLength);
    }
    /**
     * exponentiation (a^x)
     *   y = pow(a,x)
     *   x = log(a,y)
     * ONE = pow(a,0)
     *   a = pow(a,1)
     */
    pow(a, x) {
        throw new Error("not supported operation");
    }
    /**
     * pow(ALPHA,x) = exp(x)
     *          ONE = exp(0)
     *        ALPHA = exp(1)
     */
    exp(x) {
        throw new Error("not supported operation");
    }
}
export class Gf2PrimeArrayPolynomialRing {
    /**
     *
     */
    constructor() {
        this._nan = [Number.NaN];
    }
    get field() {
        if (this._field)
            return this._field;
        this._field = new GfPrime(2);
        return this._field;
    }
    listPx(nth, listAll = false) {
        return Gf2PrimeFieldHelper.listPx(nth, listAll).map(x => Gf2PrimeFieldHelper.bitToPoly(x));
    }
    getAt(x, index) {
        return x[index];
    }
    setAt(x, index, v) {
        x[index] = v;
        return x;
    }
    slice(x, start, end) {
        return x.slice(start, end);
    }
    map(x, fn) {
        return this.mapToSelf(Array.from(x), fn);
    }
    mapToSelf(x, fn) {
        for (let i = 0; i < x.length; i++)
            x[i] = fn(x[i]);
        return x;
    }
    newNthPolynomial(n) {
        const r = [];
        r.length = n + 1;
        r.fill(0);
        return r;
    }
    /**
     * additive identity element
     */
    get ZERO() {
        return [0];
    }
    /**
     * multiplicative identity element
     */
    get ONE() {
        throw [1];
    }
    /**
     * NaN value
     */
    get NaN() {
        return this._nan;
    }
    /**
     * normalize a value
     */
    normalize(x) {
        return (Array.isArray(x) ? x : Array.from(x)).map(v => Boolean(v & 1) ? 1 : 0);
    }
    /**
     * check value is NaN
     */
    isNaN(x) {
        if (x !== this.NaN) {
            if (x.length === 1 && x[0] === Number.NaN)
                return true;
            return false;
        }
        return true;
    }
    /**
     * compare element and return true if x equals to y
     * eq(v,v)     -> true
     * eq(NaN,NaN) -> false
     */
    eq(x, y) {
        if (this.isNaN(x) || this.isNaN(y))
            return false;
        const len = Math.max(x.length, y.length);
        for (let i = 0; i < len; i++) {
            if ((x[i] !== void (0) ? (Boolean(x[i] & 1) ? 1 : 0) : 0) !==
                (y[i] !== void (0) ? (Boolean(y[i] & 1) ? 1 : 0) : 0))
                return false;
        }
        return true;
    }
    /**
     * compare element and return false if x equals to y
     */
    noteq(x, y) {
        return !this.eq(x, y);
    }
    /**
     * negate; additive inverse (-x)
     */
    neg(x) {
        return Array.from(x);
    }
    /**
     * addition (x+y)
     */
    add(x, y) {
        return Gf2PrimeFieldHelper.polyAdd(x, y);
    }
    /**
     * subtraction (x-y)
     */
    sub(x, y) {
        return Gf2PrimeFieldHelper.polySub(x, y);
    }
    /**
     * multiplication (x*y)
     */
    mul(x, y) {
        return Gf2PrimeFieldHelper.polyMul(x, y);
    }
    /**
     * modulation (x%y)
     */
    mod(x, y, extendDividendLength = 0) {
        return Gf2PrimeFieldHelper.polyDiv(x, y, extendDividendLength).r;
    }
    /**
     * division (x/y)
     *             ? = div(x,ZERO) ; depends on field definition
     * mul(x,inv(y)) = div(x,y)    ;  when  y != ZERO
     */
    divmod(x, y, extendDividendLength = 0) {
        return Gf2PrimeFieldHelper.polyDiv(x, y, extendDividendLength);
    }
    /**
     * exponentiation (a^x)
     *   y = pow(a,x)
     *   x = log(a,y)
     * ONE = pow(a,0)
     *   a = pow(a,1)
     */
    pow(a, x) {
        throw new Error("not supported operation");
    }
    /**
     * pow(ALPHA,x) = exp(x)
     *          ONE = exp(0)
     *        ALPHA = exp(1)
     */
    exp(x) {
        throw new Error("not supported operation");
    }
}
//# sourceMappingURL=Gf2PrimeHelper.js.map